<template>
  <div class="projet-part">
    <h2 id="plus">{{ $t("projetPage.labellisation.plus.titre") }}</h2>

    <p class="info">{{ $t("projetPage.labellisation.plus.intro") }}</p>

    <InputWithValidation
      v-model="localNbSortiesSite"
      :validation-activated="validationActivated"
      :show-validation-errors="showValidationErrors"
      rules="numeric|required"
      type="text"
      :label="
        projetAnnuel.typeDossier !== 'DOSSIER_RENOUVELLEMENT'
          ? $t('projetPage.labellisation.plus.nb-sorties-label')
          : $t('projetPage.labellisation.plus.nb-sorties-label-lourd')
      "
      :disabled="readOnly"
      vid="plus_nbSortiesSite"
      @manual-change="modified()"
    />
    <InputWithValidation
      v-if="projetAnnuel.typeDossier !== 'DOSSIER_RENOUVELLEMENT'"
      v-model="localNbTotalPresenceReferent"
      :validation-activated="validationActivated"
      :show-validation-errors="showValidationErrors"
      rules="numeric|required"
      type="text"
      :label="$t('projetPage.labellisation.plus.nb-presence-referent-label')"
      :disabled="readOnly"
      vid="plus_nbTotalPresenceReferent"
      @manual-change="modified()"
    />
    <InputWithValidation
      v-else
      v-model="localCmtFonctionnementBinome"
      :validation-activated="validationActivated"
      :show-validation-errors="showValidationErrors"
      rules="required"
      type="textarea"
      :label="$t('projetPage.labellisation.plus.nb-presence-referent-label-lourd')"
      :disabled="readOnly"
      vid="plus_cmtFonctionnementBinome"
      @manual-change="modified()"
    />
    <SelectWithValidation
      v-if="projetAnnuel.typeDossier !== 'DOSSIER_RENOUVELLEMENT'"
      v-model="localTempsPasseProjet"
      :validation-activated="validationActivated"
      :show-validation-errors="showValidationErrors"
      :label="$t('projetPage.labellisation.plus.temps-passe-projet')"
      :data="tempsPasseProjet"
      :select-labels="tempsPasseProjetLabels"
      rules="required"
      :disabled="readOnly"
      :vid="'plus_tempsPasseProjet'"
      @manual-change="modified()"
    />
    <InputWithValidation
      v-else
      v-model="localCmtProjetEtablissement"
      :validation-activated="validationActivated"
      :show-validation-errors="showValidationErrors"
      rules="required"
      type="textarea"
      :label="$t('projetPage.labellisation.plus.temps-passe-projet-lourd')"
      :disabled="readOnly"
      vid="plus_cmtProjetEtablissement"
      @manual-change="modified()"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue"
import { ProjetAnnuelDTO, TempsPasse } from "@/model/bean/GeneratedDTOs"
import InputWithValidation from "@/views/inputs/InputWithValidation.vue"
import SelectWithValidation from "@/views/inputs/SelectWithValidation.vue"
import { TEMPS_PASSE } from "@/model/bean/EnumHelper"

export default defineComponent({
  name: "PlusView",
  components: {
    InputWithValidation,
    SelectWithValidation,
  },
  props: {
    projetAnnuel: {
      type: Object as PropType<ProjetAnnuelDTO>,
      required: true,
    },
    readOnly: {
      type: Boolean,
      required: false,
    },
    validationActivated: {
      type: Boolean,
      default: true,
    },
    showValidationErrors: {
      type: Boolean,
      default: true,
    },
  },

  emits: ["projet-annuel-updated", "dirtify-project", "trigger-validation"],
  data() {
    return {
      tempsPasseProjet: Object.values(TEMPS_PASSE),
      tempsPasseProjetLabels: Object.values(TEMPS_PASSE).map((value) => {
        return this.$t(
          `projetPage.labellisation.plus.temps-passe-projet-labels.${value}`,
        ).toString()
      }),
    }
  },
  computed: {
    localNbSortiesSite: {
      get(): number {
        return this.projetAnnuel.nbSortiesSite
      },
      set(value: number): void {
        this.updateProjetAnnuel("nbSortiesSite", value)
      },
    },
    localNbTotalPresenceReferent: {
      get(): number {
        return this.projetAnnuel.nbTotalPresenceReferent
      },
      set(value: number): void {
        this.updateProjetAnnuel("nbTotalPresenceReferent", value)
      },
    },
    localCmtFonctionnementBinome: {
      get(): string {
        return this.projetAnnuel.cmtFonctionnementBinome
      },
      set(value: string): void {
        this.updateProjetAnnuel("cmtFonctionnementBinome", value)
      },
    },
    localTempsPasseProjet: {
      get(): TempsPasse {
        return this.projetAnnuel.tempsPasseProjet
      },
      set(value: TempsPasse): void {
        this.updateProjetAnnuel("tempsPasseProjet", value)
      },
    },
    localCmtProjetEtablissement: {
      get(): string {
        return this.projetAnnuel.cmtProjetEtablissement
      },
      set(value: string): void {
        this.updateProjetAnnuel("cmtProjetEtablissement", value)
      },
    },
  },
  methods: {
    modified(): void {
      this.$emit("dirtify-project")
      this.$emit("trigger-validation")
    },
    updateProjetAnnuel<K extends keyof ProjetAnnuelDTO>(
      attributeName: K,
      value: ProjetAnnuelDTO[K],
    ): void {
      const updatedProjet = { ...this.projetAnnuel }
      updatedProjet[attributeName] = value
      this.$emit("projet-annuel-updated", updatedProjet)
    },
  },
})
</script>

<style lang="scss" scoped>
.projet-part h2 {
  padding-bottom: 0;
}
</style>
